import { FC, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { clientConfig } from '@/config/clientConfig';
import { COLORS } from '@/shared/constants/colors';
import { DropdownMenu, MenuItem } from '@/shared/uikit/DropdownMenu';
import { Icon } from '@/shared/uikit/Icon';

import { FileInfo } from '../types';

import { Status } from './Status';

type FileItemProps = {
  fileInfo: FileInfo;
  index: number;
  onDetails: () => void;
  onDownload: () => void;
  onAttach: () => void;
  onEditComment: () => void;
  onRemove: () => void;
};

export const FileItem: FC<FileItemProps> = ({
  fileInfo,
  onDetails,
  onDownload,
  onAttach,
  onEditComment,
  onRemove,
}) => {
  const { t } = useTranslation('entities/inbox', { keyPrefix: 'emails' });

  const isReadonlyMode = clientConfig.isExported;

  const itemActions = useMemo(() => {
    const items = [
      { label: t('actions.details'), icon: 'list', onClick: onDetails },
      { label: t('actions.download'), icon: 'download-simple', onClick: onDownload },
      {
        label: t('actions.attach'),
        icon: 'paperclip',
        onClick: onAttach,
        // hide: isReadonlyMode,
        hide: true,
      },
      {
        label: t('actions.editComment'),
        icon: 'chat-text',
        onClick: onEditComment,
        hide: isReadonlyMode,
      },
      {
        label: t('actions.delete'),
        icon: 'trash',
        onClick: onRemove,
        color: 'red',
        hide: isReadonlyMode,
      },
    ] satisfies (MenuItem & { hide?: boolean })[];

    return items.filter((item) => !item.hide);
  }, [fileInfo]);

  return (
    <Container>
      <Status fileInfo={fileInfo} />
      <Info>
        <span style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          <span className="truncate" style={{ fontWeight: 700, maxWidth: 'calc(100% - 70px)' }}>
            {fileInfo.name}
          </span>
          {/* &nbsp;•&nbsp;
          <span>-</span> */}
        </span>
        <span style={{ fontSize: 12 }}>
          <span style={{ overflow: 'hidden' }}>{fileInfo.comment || '-'}</span>
        </span>
      </Info>
      <Actions>
        <DropdownMenu items={itemActions}>
          <MenuButton>
            <Icon name="dots-three-vertical" size="s20" />
          </MenuButton>
        </DropdownMenu>
      </Actions>
    </Container>
  );
};

const Container = styled.div`
  min-height: 64px;
  padding: 14px 8px;
  border-bottom: 1px solid ${COLORS.systemDark100};

  display: grid;
  grid-template-columns: 28px 1fr 36px;
  gap: 8px;
  align-items: flex-start;

  & .errorMessage {
    color: ${COLORS.systemFlame200};
  }
`;

const Info = styled.div`
  overflow: hidden;
  display: grid;
  gap: 4px;
`;

const Actions = styled.div``;

const MenuButton = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.systemLight200};
  cursor: pointer;
`;
